import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import base from "../apis";
import { IN_HOUSE_REPORTS } from "../actions/types";
import {
  fetchAgentInHouseReports,
  getInHouseNotificationCandidates,
  getInHouseNotificationPreference,
  getInHouseReportFingerprint,
} from "../utils/inHouseReports";

const POLL_INTERVAL = 60000;
const REPORT_FETCH_LIMIT = 25;

function InHouseReportNotifications() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const role = useSelector((state) => state.auth.role);
  const agentId = useSelector((state) => state.auth.typeId);
  const trackedPendingReports = useSelector(
    (state) => state.inHouseReportNotifications.trackedPendingReports,
  );
  const reportNotificationEnabled = useSelector(
    (state) => state.inHouseReportNotifications.reportNotificationEnabled,
  );

  const trackedPendingReportsRef = useRef(trackedPendingReports);
  const reportNotificationEnabledRef = useRef(reportNotificationEnabled);

  useEffect(() => {
    trackedPendingReportsRef.current = trackedPendingReports;
  }, [trackedPendingReports]);

  useEffect(() => {
    reportNotificationEnabledRef.current = reportNotificationEnabled;
  }, [reportNotificationEnabled]);

  useEffect(() => {
    const isAgent = role === "agent" || role === "realty_agent";

    if (!isAgent || !agentId) return undefined;

    let cancelled = false;
    let intervalId;
    let isFirstFetch = true;

    const fetchNotificationPreference = async () => {
      try {
        const { data } = await base.get(`register/leads_notification/${agentId}/`);
        const preference = getInHouseNotificationPreference(data);

        if (!cancelled && preference !== null) {
          dispatch({
            type: "SET_IN_HOUSE_REPORT_NOTIFICATION_PREFERENCE",
            payload: preference,
          });
        }
      } catch (error) {}
    };

    const pollInHouseReports = async () => {
      try {
        const { results } = await fetchAgentInHouseReports({
          apiClient: base,
          agentId,
          page: 1,
          limit: REPORT_FETCH_LIMIT,
        });

        if (cancelled) return;

        const trackedReports = getInHouseNotificationCandidates(results).reduce(
          (acc, report) => {
            if (!report?.id) return acc;

            acc[report.id] = getInHouseReportFingerprint(report);
            return acc;
          },
          {},
        );

        const nextTrackedIds = Object.keys(trackedReports);

        if (
          isFirstFetch &&
          !Object.keys(trackedPendingReportsRef.current || {}).length
        ) {
          dispatch({
            type: "INITIALIZE_IN_HOUSE_REPORT_NOTIFICATIONS",
            payload: trackedReports,
          });
          isFirstFetch = false;
          return;
        }

        isFirstFetch = false;

        const newReportIds = nextTrackedIds.filter(
          (id) => trackedPendingReportsRef.current?.[id] !== trackedReports[id],
        );

        dispatch({
          type: "SYNC_IN_HOUSE_REPORT_NOTIFICATIONS_TRACKER",
          payload: trackedReports,
        });

        if (!newReportIds.length) return;

        dispatch({
          type: "RECEIVE_IN_HOUSE_REPORT_NOTIFICATIONS",
          payload: newReportIds,
        });

        if (!reportNotificationEnabledRef.current) return;

        const message =
          newReportIds.length === 1
            ? "A new in-house BPO report is waiting for your response."
            : `${newReportIds.length} in-house BPO reports are waiting for your response.`;

        toast.info(message, {
          toastId: `in-house-report-${newReportIds.join("-")}`,
          onClick: () => {
            dispatch({ type: IN_HOUSE_REPORTS });
            navigate("/bpo-report");
          },
        });
      } catch (error) {}
    };

    fetchNotificationPreference();
    pollInHouseReports();
    intervalId = window.setInterval(pollInHouseReports, POLL_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [agentId, dispatch, navigate, role]);

  return null;
}

export default InHouseReportNotifications;
